import { useEffect, useState } from "react";

import DeleteRounded from "@mui/icons-material/DeleteRounded";
import { Box, Button, IconButton, MenuItem, Select, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

import { AuthState } from "context/AuthContext";
import { GlobalState } from "context/GlobalContext";
import { PimDashboardState } from "context/PimDashboardContext";

import { fabricTableColumns } from "../../data";
import { inputTypes } from "data/globalData";
import { WhiteBgBox } from "../TrimsTable/TrimsTable";

const colStyle = {
	headerAlign: "center",
	align: "center",
	width: 133,
};

const fabricComponentOptions = ["SHELL FABRIC", "CONTRAST FABRIC", "TRIM FABRIC", "LINING"];

const escapeRegExp = (value = "") => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getNextFabricTypeName = (baseType = "", rows = []) => {
	const normalizedBaseType = baseType?.trim();
	if (!normalizedBaseType) return "";

	const componentPattern = new RegExp(`^${escapeRegExp(normalizedBaseType)}(?:\\s+(\\d+))?$`);
	const nextIndex =
		rows.reduce((maxIndex, row) => {
			const currentType = row?.[fabricTableColumns.type.key] ?? "";
			const matchedType = currentType.match(componentPattern);
			if (!matchedType) return maxIndex;
			const currentIndex = matchedType[1] ? Number(matchedType[1]) : 1;
			return Math.max(maxIndex, currentIndex);
		}, 0) + 1;

	return nextIndex === 1 ? normalizedBaseType : `${normalizedBaseType} ${nextIndex}`;
};

const valueOptions = {
	[fabricTableColumns.type.key]: fabricComponentOptions,
	[fabricTableColumns.blend.key]: ["COTTON RAYON", "85% VISCOS 15% NYLON", "96% POLY 4% LYCRA"],
	[fabricTableColumns.construction.key]: ["COTTON RAYON", "85% VISCOS 15% NYLON", "96% POLY 4% LYCRA"],
	[fabricTableColumns.fabric_category.key]: ["BEDFORD", "NONWOVEN", "VELVETTE"],
	[fabricTableColumns.source_category.key]: ["DENIM", "MILL MADE", "PROCESS", "DIGITAL PRINT"],
	[fabricTableColumns.weave.key]: ["PLAIN", "MIXED STOCK", "OXFORD", "PLAIN SORT NO PD2261"],
	[fabricTableColumns.width.key]: ["110CM", "112CM", '24"D', "122", "144"],
	[fabricTableColumns.consumption_unit.key]: ["Mtr", "Yard", "Kg"],
};

const FabricTable = ({ styleFabricArr, updatedFabricArr, setUpdatedFabricArr, publishDisabled }) => {
	const { setSnackBar, setSnackBarOpen, snackBar } = GlobalState();
	const { fabmaster, setFabMaster } = PimDashboardState();
	const { user } = AuthState();
	const defFabricTableColumns = [
		{
			...fabricTableColumns.type,
			...colStyle,
			width: 120,
			headerName: fabricTableColumns.type.name,
			field: fabricTableColumns.type.key,
		},
		{
			...fabricTableColumns.blend,
			type: "singleSelect",
			editable: true,
			valueOptions: valueOptions[fabricTableColumns.blend.key],
			...colStyle,
			headerName: fabricTableColumns.blend.name,
			field: fabricTableColumns.blend.key,
			renderCell: ({ field, row }) => {
				return <WhiteBgBox field={field} row={row} />;
			},
		},
		{
			...fabricTableColumns.source_category,
			field: fabricTableColumns.source_category.key,
			headerName: fabricTableColumns.source_category.name,
			editable: true,
			type: "singleSelect",
			valueOptions: valueOptions[fabricTableColumns.source_category.key],
			editable: true,
			...colStyle,
			renderCell: ({ field, row }) => {
				return <WhiteBgBox field={field} row={row} />;
			},
		},
		{
			...fabricTableColumns.fabric_category,
			field: fabricTableColumns.fabric_category.key,
			headerName: fabricTableColumns.fabric_category.name,
			editable: true,
			type: "singleSelect",
			valueOptions: valueOptions[fabricTableColumns.fabric_category.key],
			...colStyle,
			renderCell: ({ field, row }) => {
				return <WhiteBgBox field={field} row={row} />;
			},
		},
		{
			...fabricTableColumns.weave,
			field: fabricTableColumns.weave.key,
			editable: true,
			headerName: fabricTableColumns.weave.name,
			type: "singleSelect",
			valueOptions: valueOptions[fabricTableColumns.weave.key],

			...colStyle,
			renderCell: ({ field, row }) => {
				return <WhiteBgBox field={field} row={row} />;
			},
		},
		{
			...fabricTableColumns.construction,
			field: fabricTableColumns.construction.key,
			editable: true,
			headerName: fabricTableColumns.construction.name,
			type: "singleSelect",
			valueOptions: valueOptions[fabricTableColumns.construction.key],

			...colStyle,
			renderCell: ({ field, row }) => {
				return <WhiteBgBox field={field} row={row} />;
			},
		},
		{
			...fabricTableColumns.width,
			field: fabricTableColumns.width.key,
			headerName: fabricTableColumns.width.name,
			editable: true,
			type: "singleSelect",
			valueOptions: valueOptions[fabricTableColumns.width.key],

			...colStyle,
			renderCell: ({ field, row }) => {
				return <WhiteBgBox field={field} row={row} />;
			},
		},
		{
			...fabricTableColumns.consumption,
			field: fabricTableColumns.consumption.key,
			headerName: fabricTableColumns.consumption.name,
			editable: true,
			type: "number",

			...colStyle,
			renderCell: ({ field, row }) => {
				return <WhiteBgBox field={field} row={row} />;
			},
		},
		{
			...fabricTableColumns.consumption_unit,
			field: fabricTableColumns.consumption_unit.key,
			headerName: fabricTableColumns.consumption_unit.name,
			editable: true,
			type: "singleSelect",
			valueOptions: valueOptions[fabricTableColumns.consumption_unit.key],
			...colStyle,
			renderCell: ({ field, row }) => {
				return <WhiteBgBox field={field} row={row} />;
			},
		},
		{
			headerName: "Delete",
			width: 120,
			...colStyle,
			renderCell: ({ row }) => {
				return (
					<IconButton onClick={() => handleDeleteFabricClick(row)} disabled={publishDisabled}>
						<DeleteRounded />
					</IconButton>
				);
			},
		},
	];
	const [fabricColumns, setFabricColumns] = useState(defFabricTableColumns);
	const [fabricArr, setFabricArr] = useState(styleFabricArr?.length ? styleFabricArr : []);

	useEffect(() => {
		const fetch = async () => {
			try {
				const res = await axios.get("/api/master/get_fabric_master");
				setFabMaster(res.data);
				const obj = res?.data?.reduce(
					(acc, row) => {
						Object.keys(fabricTableColumns).forEach((col) => {
							if (row[col]) acc[col].push(row[col]);
						});
						return acc;
					},
					{
						...Object.keys(fabricTableColumns).reduce((acc, row) => {
							acc[row] = [];
							return acc;
						}, {}),
					}
				);
				setFabricColumns([
					...defFabricTableColumns?.map((row) => {
						if (obj[row?.key]?.length > 0)
							return {
								...row,
								valueOptions: [inputTypes.SELECT.key, ...new Set([...obj[row?.key]])],
							};
						else return row;
					}),
				]);
			} catch (error) {
				console.log(error);
				console.log("unable to get fabric data");
			}
		};
		if (!fabmaster) fetch();
		else {
			const obj = fabmaster?.reduce(
				(acc, row) => {
					Object.keys(fabricTableColumns).forEach((col) => {
						if (row[col]) acc[col].push(row[col]);
					});
					return acc;
				},
				{
					...Object.keys(fabricTableColumns).reduce((acc, row) => {
						acc[row] = [];
						return acc;
					}, {}),
				}
			);
			setFabricColumns([
				...defFabricTableColumns?.map((row) => {
					if (obj[row?.key]?.length > 0)
						return { ...row, valueOptions: [inputTypes.SELECT.key, ...new Set([...obj[row?.key]])] };
					else return row;
				}),
			]);
		}
	}, []);

	const defFabricObj = Object.keys(fabricTableColumns).reduce((acc, row) => {
		if (fabricTableColumns[row]?.input_type === inputTypes.SELECT.key) acc[fabricTableColumns[row].key] = inputTypes.SELECT.key;
		return acc;
	}, {});

	const [fabricToAdd, setFabricToAdd] = useState(defFabricObj);
	const handleAddFabricClick = () => {
		let obj = fabricToAdd;
		if (!obj || !obj[fabricTableColumns.type.key] || obj[fabricTableColumns.type.key] === inputTypes.SELECT.key) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `Select Fabric Type `,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}
		const nextFabricType = getNextFabricTypeName(obj[fabricTableColumns.type.key], fabricArr);
		let objToAdd = {
			...fabricToAdd,
			[fabricTableColumns.type.key]: nextFabricType,
			is_new: true,
			id: new Date().valueOf(),
		};
		if (fabricArr) setFabricArr([...fabricArr, objToAdd]);
		else setFabricArr([objToAdd]);
		setUpdatedFabricArr([...updatedFabricArr, objToAdd]);
	};
	const handleDeleteFabricClick = async (row) => {
		try {
			if (!row.is_new) {
				await axios.delete(`/api/fabric_trims/delete_fabric/${row.id}`).then((res) => {
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						message: `Fabric Deleted `,
						severity: "success",
						anchorOrigin: { horizontal: "center", vertical: "top" },
					});
				});
			} else {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: `Fabric Deleted `,
					severity: "success",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
			}
			setFabricArr(fabricArr.filter((fab) => fab?.id !== row.id));
			setUpdatedFabricArr(updatedFabricArr?.filter((fab) => fab?.id !== row.id));
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `Error Occured `,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};
	console.log(updatedFabricArr, "updatedFabricArr");

	const handleRowChange = (updatedRow, originalRow) => {
		const rowToAdd = { ...updatedRow, updated_by: user?.email };
		if (updatedFabricArr?.length)
			setUpdatedFabricArr([...updatedFabricArr?.filter((row) => row?.id !== originalRow?.id), rowToAdd]);
		else setUpdatedFabricArr([rowToAdd]);

		setFabricArr(
			fabricArr?.map((row) => {
				if (row?.id === originalRow?.id) return rowToAdd;
				else return row;
			})
		);
		return rowToAdd;
	};

	return (
		<Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" rowGap={1}>
				<Typography pb={0} variant="h6">
					Fabric Sheet
				</Typography>
				<Box display="flex" pb={1} pt={1} justifyContent="space-between">
					<Box
						sx={{
							alignSelf: "flex-start",
							display: "flex",
							flexWrap: "wrap",
							alignItems: "center",
							columnGap: 1,
						}}>
						<Select
							sx={{ width: 170 }}
							size="small"
							fullWidth
							variant="outlined"
							MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
							value={
								fabricToAdd && fabricToAdd[fabricTableColumns.type.key]
									? fabricToAdd[fabricTableColumns.type.key]
									: inputTypes.SELECT.key
							}
							onChange={(e) => setFabricToAdd({ ...fabricToAdd, [fabricTableColumns.type.key]: e.target.value })}>
							<MenuItem value={inputTypes.SELECT.key}>
								<span style={{ color: grey[400] }}>Select Type</span>
							</MenuItem>
							{valueOptions[fabricTableColumns.type.key]?.map((row) => {
								return (
									<MenuItem key={row} value={row}>
										{row}
									</MenuItem>
								);
							})}
						</Select>

						<Box>
							<Button size="small" onClick={handleAddFabricClick} variant="contained">
								Add Fabric
							</Button>
						</Box>
					</Box>
				</Box>
			</Box>
			<div width="100%">
				<DataGrid
					columns={fabricColumns}
					key={fabmaster && fabricColumns}
					rows={fabricArr ? fabricArr : []}
					slotProps={{
						baseSelect: {
							MenuProps: {
								PaperProps: {
									sx: {
										maxHeight: 250,
										width: 200,
										// overflow: "scroll",
									},
								},
							},
						},
					}}
					sx={{
						height: 170,
						background: grey[200],
						// border: "none",
						"& .MuiDataGrid-row": {
							// borderBottom: "1px solid lightgray",
							cursor: "pointer",
							// border: "none",
						},
						"& .MuiDataGrid-columnHeader": {
							cursor: "pointer",
						},
						"& .MuiDataGrid-cell": {
							padding: 0,
						},
						"& .MuiDataGrid-cell:focus-within": {
							outline: `solid`,
						},
						"& .MuiDataGrid-columnHeaders": {
							background: grey[100],
							// mb: 2,
							// border: "none",
							borderRadius: 1,
							// padding: "10px 0px",
						},
					}}
					columnHeaderHeight={35}
					rowHeight={35}
					showCellVerticalBorder
					initialState={{
						pagination: {
							paginationModel: { pageSize: 10 },
						},
					}}
					processRowUpdate={handleRowChange}
					hideFooter
				/>
			</div>
		</Box>
	);
};

export default FabricTable;
